import * as React from 'react';
import {
  CircularProgress,
  MenuItem,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Table,
  TextField,
  Chip,
} from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AdminPageLayout,
  AdminSectionCard,
  TableRowActions,
  DeleteConfirmationDialog,
  AdminAction,
} from '../../components/admin';
import { StyledTableCell, StyledTableRow } from '../../utils/constantData';
import { getAdminProfile } from '../../utils/auth';
import AdminUserViewDialog from './AdminUserViewDialog';
import { getAdminUsers, deleteAdminUser, updateAdminUser } from '.';

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Editor', label: 'Editor' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fafaf9',
  },
};

const COLUMN_COUNT = 9;

function formatDate(iso) {
  return iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
}

function formatDateTime(iso) {
  return iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
}

export default function AdminUsersListPage() {
  const navigate = useNavigate();
  const currentUserId = getAdminProfile()?.id;

  const [users, setUsers] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');

  const hasActiveFilters = !!(search || roleFilter || statusFilter);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [viewTarget, setViewTarget] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [togglingId, setTogglingId] = React.useState(null);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUsers({
        search,
        role: roleFilter,
        status: statusFilter,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (res.status === 200) {
        setUsers(res.data.data.users);
        setTotal(res.data.data.total);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('You do not have permission to access admin users.');
        navigate('/users', { replace: true });
      } else {
        toast.error('Failed to load users.');
      }
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, page, rowsPerPage, navigate]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  React.useEffect(() => {
    setPage(0);
  }, [search, roleFilter, statusFilter]);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAdminUser(deleteTarget.id);
      toast.success(`"${deleteTarget.fullName}" deleted successfully.`);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete user. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const nextStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    setTogglingId(user.id);
    try {
      await updateAdminUser(user.id, { status: nextStatus });
      toast.success(
        `"${user.fullName}" ${nextStatus === 'Active' ? 'activated' : 'deactivated'} successfully.`
      );
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update user status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <AdminPageLayout
        eyebrow="Users"
        title="Admin Users"
        description="Manage backend admin panel users with role-based access control."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <TextField
              id="admin-users-search"
              label="Search by name, username, or email"
              value={search}
              size="small"
              sx={{ minWidth: 260, ...inputSx }}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              id="admin-users-role-filter"
              select
              label="Role"
              value={roleFilter}
              size="small"
              sx={{ minWidth: 140, ...inputSx }}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              id="admin-users-status-filter"
              select
              label="Status"
              value={statusFilter}
              size="small"
              sx={{ minWidth: 140, ...inputSx }}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <AdminAction
              id="admin-users-reset-btn"
              variant="outline"
              startIcon={<RestartAlt />}
              disabled={!hasActiveFilters}
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
                setPage(0);
              }}
              sx={{
                whiteSpace: 'nowrap',
                borderColor: hasActiveFilters ? 'rgba(63,122,42,0.4)' : undefined,
                color: hasActiveFilters ? '#3f7a2a' : undefined,
                '&:hover': hasActiveFilters ? { borderColor: '#3f7a2a', background: 'rgba(63,122,42,0.06)' } : {},
              }}
            >
              Reset
            </AdminAction>
            <AdminAction
              id="admin-users-add-btn"
              onClick={() => navigate('/admin-users/new')}
            >
              + Add New User
            </AdminAction>
          </div>
        }
      >
        <AdminSectionCard
          title=""
          description=""
          className="admin-table-shell"
        >
          <TableContainer component="div">
            <Table sx={{ minWidth: 960 }} aria-label="admin panel users table">
              <TableHead>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Full Name</StyledTableCell>
                <StyledTableCell>Username</StyledTableCell>
                <StyledTableCell>Email Address</StyledTableCell>
                <StyledTableCell>Role</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Last Login</StyledTableCell>
                {/* <StyledTableCell>Created Date</StyledTableCell> */}
                <StyledTableCell align="right">Actions</StyledTableCell>
              </TableHead>
              <TableBody>
                {loading ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT} style={{ textAlign: 'center', padding: '32px' }}>
                      <CircularProgress size={20} sx={{ color: '#78716c' }} />
                      <span style={{ marginLeft: 8, color: '#78716c', fontSize: 14 }}>Loading...</span>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      style={{ textAlign: 'center', padding: '32px', color: '#78716c', fontSize: 14 }}
                    >
                      No admin users found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => {
                    const isSelf = currentUserId != null && user.id === currentUserId;
                    const isToggling = togglingId === user.id;

                    return (
                      <StyledTableRow key={user.id}>
                        <StyledTableCell component="th">
                          {page * rowsPerPage + index + 1}
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 500 }}>{user.fullName}</StyledTableCell>
                        <StyledTableCell sx={{ color: '#78716c', fontFamily: 'monospace', fontSize: '0.82rem' }}>
                          @{user.username}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#57534e' }}>{user.email}</StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={user.role}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              backgroundColor: user.role === 'Admin' ? '#1c1917' : '#e7e5e4',
                              color: user.role === 'Admin' ? '#fff' : '#44403c',
                              borderRadius: '6px',
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={user.status}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              backgroundColor: user.status === 'Active' ? '#dcfce7' : '#fef2f2',
                              color: user.status === 'Active' ? '#166534' : '#991b1b',
                              borderRadius: '6px',
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#57534e', fontSize: '0.82rem' }}>
                          {formatDateTime(user.lastLoginAt)}
                        </StyledTableCell>
                        {/* <StyledTableCell>{formatDate(user.createdAt)}</StyledTableCell> */}
                        <StyledTableCell align="right">
                          <TableRowActions
                            rowId={user.id}
                            onView={() => setViewTarget(user)}
                            onEdit={() => navigate(`/admin-users/${user.id}`)}
                            onDelete={() => setDeleteTarget(user)}
                            status={{
                              isActive: user.status === 'Active',
                              onToggle: () => handleToggleStatus(user),
                              loading: isToggling,
                              disabled: isSelf,
                            }}
                            disabled={{ delete: isSelf }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ '& .MuiTablePagination-toolbar': { borderTop: '1px solid #e7e5e4' } }}
          />
        </AdminSectionCard>
      </AdminPageLayout>

      <AdminUserViewDialog
        user={viewTarget}
        open={!!viewTarget}
        onClose={() => setViewTarget(null)}
        onEdit={(user) => {
          setViewTarget(null);
          navigate(`/admin-users/${user.id}`);
        }}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        itemName={deleteTarget?.fullName}
        loading={deleting}
      />
    </>
  );
}
